import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link, useParams } from "react-router-dom";
import { MaskedLines, Reveal } from "../components/Reveal";
import PageHero from "../components/PageHero";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { wordpressApi } from "../lib/api";
import { ArrowUpRight, Clock, GraduationCap, Download } from "lucide-react";
import SEO, { JsonLd, optimizeMetaDescription, optimizeMetaTitle } from "../components/SEO";

function stripHtml(html) {
  const div = document.createElement("div");
  div.innerHTML = html || "";
  return div.textContent || div.innerText || "";
}

export function CoursesIndex() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    wordpressApi
      .get("", {
        params: {
          endpoint: "posts",
          categories: 49,
          per_page: 100,
          _embed: true,
        },
      })
      .then((r) => {
        const posts = r.data || [];

        const courses = posts.map((post) => ({
          id: String(post.id),
          title: post.title?.rendered || "",
          slug: post.slug || "",
          description: stripHtml(
            post.excerpt?.rendered ||
            post.content?.rendered ||
            ""
          ),
          cover_image:
            post._embedded?.["wp:featuredmedia"]?.[0]?.source_url || "",
          price: post.meta?.course_price || "",
          duration: post.meta?.course_duration || "",
          level: post.meta?.course_level || "",
          instructor:
            post.meta?.course_instructor || "Dorado9 Team",
          syllabus_url:
            post.meta?.course_syllabus_url || "",
          modules:
            post.meta?.course_modules || [],
          curriculum:
            post.meta?.course_curriculum || [],
          tools:
            post.meta?.course_tools || [],
          why_choose_intro:
            post.meta?.course_why_choose_intro || "",
          why_choose:
            post.meta?.course_why_choose || [],
          student_work:
            post.meta?.course_student_work || [],
          faqs:
            post.meta?.course_faqs || [],
          seo_title: post.meta?.seo_title || post.meta?.meta_title || "",
          seo_description: post.meta?.seo_description || post.meta?.meta_description || "",
        }));

        setItems(courses);
      })
      .catch((error) => {
        console.error("Failed to load WordPress courses:", error);
        setItems([]);
      });
  }, []);

  return (
    <>
      <PageHero
        eyebrow="The Dorado9 Academy"
        image="https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=1920&q=60&auto=format&fit=crop"
        lines={["Learn from", <>the <span className="font-serif-accent text-brand-purple lowercase">practice.</span></>]}
      >
        <p className="mt-8 text-zinc-400 max-w-xl">Real playbooks from our client work. Live cohorts, no fluff, and a certificate that means something.</p>
      </PageHero>

      <section className="bg-brand-bg py-16 lg:py-24 px-6 lg:px-12 border-t border-zinc-900">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-10">
          {items.map((c, i) => (
            <Reveal key={c.id} delay={i * 0.08}>
              <Link to={`/courses/${c.slug}`} data-testid={`course-card-${c.slug}`} className="group block spotlight-card border border-zinc-900 hover:border-brand-mint/40 transition-colors duration-500 overflow-hidden">
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={c.cover_image} alt={c.title} className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-[1200ms]" />
                </div>
                <div className="p-8 lg:p-10 relative z-10">
                  <div className="flex items-center gap-4 mb-4">
                    <span className="text-xs text-brand-mint uppercase tracking-[0.24em]">{c.level}</span>
                    <span className="w-1 h-1 rounded-full bg-zinc-700" />
                    <span className="text-xs text-zinc-500 uppercase tracking-[0.24em] inline-flex items-center gap-1.5"><Clock size={12}/> {c.duration}</span>
                  </div>
                  <h3 className="font-display text-2xl lg:text-4xl tracking-tight uppercase leading-tight">{c.title}</h3>
                  <p className="mt-4 text-zinc-400">{c.description}</p>
                  <div className="mt-8 flex items-center justify-between">
                    <span className="font-serif-accent text-brand-mint text-3xl">{c.price}</span>
                    <span className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.24em] text-zinc-300 link-underline">Enroll <ArrowUpRight size={14}/></span>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </>
  );
}

export function CourseDetail() {
  const { slug } = useParams();
  const [c, setC] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [openModules, setOpenModules] = useState([]);
  useEffect(() => {
    let active = true;

    setC(null);
    setNotFound(false);

    wordpressApi
      .get("", {
        params: {
          endpoint: "posts",
          slug: slug,
          categories: 49,
          _embed: true,
        },
      })
      .then((r) => {
        if (!active) return;

        const post = r.data?.[0];

        if (!post) {
          setNotFound(true);
          return;
        }

        const course = {
          id: String(post.id),
          title: post.title?.rendered || "",
          slug: post.slug || "",
          description: stripHtml(
            post.excerpt?.rendered ||
            post.content?.rendered ||
            ""
          ),
          cover_image:
            post._embedded?.["wp:featuredmedia"]?.[0]?.source_url || "",
          price: post.meta?.course_price || "",
          duration: post.meta?.course_duration || "",
          level: post.meta?.course_level || "",
          instructor:
            post.meta?.course_instructor || "Dorado9 Team",
          syllabus_url:
            post.meta?.course_syllabus_url || "",
          modules:
            post.meta?.course_modules || [],
          curriculum:
            post.meta?.course_curriculum || [],
          tools:
            post.meta?.course_tools || [],
          why_choose_intro:
            post.meta?.course_why_choose_intro || "",
          why_choose:
            post.meta?.course_why_choose || [],
          student_work:
            post.meta?.course_student_work || [],
          faqs:
            post.meta?.course_faqs || [],
          seo_title: post.meta?.seo_title || post.meta?.meta_title || "",
          seo_description: post.meta?.seo_description || post.meta?.meta_description || "",
        };

        setC(course);
      })
      .catch((error) => {
        console.error("Failed to load WordPress course:", error);
        if (active) setNotFound(true);
      });

    return () => {
      active = false;
    };
  }, [slug]);
  if (notFound) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 text-zinc-400">
      <p>Course not found.</p>
      <Link to="/courses" className="btn-primary">Back to courses →</Link>
    </div>
  );
  if (!c) return <div className="min-h-screen flex items-center justify-center text-zinc-500">Loading…</div>;

  const courseSchema = {
    "@context": "https://schema.org",
    "@type": "Course",
    name: c.title,
    description: c.description,
    url: `https://dorado9.com/courses/${c.slug}`,
    image: c.cover_image || undefined,
    provider: { "@id": "https://dorado9.com/#organization" },
  };

  return (
    <>
      <SEO
        schema={false}
        title={optimizeMetaTitle(c.seo_title || c.title)}
        description={optimizeMetaDescription(c.seo_description || c.description)}
      />
      <JsonLd id="course" data={courseSchema} />
      <section className="relative pt-40 lg:pt-56 pb-12 px-6 lg:px-12 bg-brand-bg">
        <div className="max-w-[1600px] mx-auto grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <Link to="/courses" className="eyebrow inline-flex items-center gap-2 mb-8 hover:text-zinc-100 transition-colors">← All courses</Link>
            <p className="eyebrow mb-4">{c.level} · {c.duration}</p>
            <h1 className="font-display text-[9vw] lg:text-[6vw] leading-[0.9] tracking-tighter uppercase">
              <MaskedLines lines={[c.title]} />
            </h1>
            <p className="mt-8 text-zinc-400 text-lg leading-relaxed max-w-xl">{c.description}</p>
          </div>
          <div className="lg:col-span-5 lg:pt-16">
            <div className="border border-zinc-900 p-8 lg:p-10 spotlight-card">
              <p className="eyebrow mb-4">Tuition</p>
              <p className="font-serif-accent text-brand-mint text-6xl lg:text-7xl leading-none">{c.price}</p>
              <p className="mt-6 text-zinc-400 text-sm">Includes live cohort, recordings, and a private community.</p>
              <Link to="/contact" className="btn-primary mt-8 w-full justify-center" data-testid="course-enroll-cta">Enroll now →</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-brand-bg py-24 lg:py-32 px-6 lg:px-12 border-t border-zinc-900">
        <div className="max-w-[1600px] mx-auto grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="eyebrow mb-4">Curriculum</p>
            <h2 className="font-display text-3xl lg:text-5xl tracking-tight uppercase">From Fundamentals <span className="font-serif-accent text-brand-mint lowercase">to real campaigns..</span></h2>
            {c.syllabus_url && (
              <a href={c.syllabus_url} target="_blank" rel="noreferrer" className="btn-ghost text-sm mt-8 inline-flex" data-testid="course-syllabus-download">
                <Download size={16} /> Download full syllabus
              </a>
            )}
          </div>
          <div className="lg:col-span-8">
            {(c.curriculum && c.curriculum.length > 0) ? (
              <Accordion
                type="multiple"
                value={openModules}
                onValueChange={(values) => {
                  const previous = openModules;

                  const clicked = c.curriculum.findIndex((_, i) => {
                    const value = `mod-${i}`;
                    return previous.includes(value) !== values.includes(value);
                  });

                  if (clicked === -1) {
                    setOpenModules(values);
                    return;
                  }

                  const isOpening = values.includes(`mod-${clicked}`);
                  const pair = clicked % 2 === 0 ? clicked + 1 : clicked - 1;

                  if (isOpening) {
                    const next = [...new Set([
                      ...values,
                      `mod-${clicked}`,
                      ...(c.curriculum[pair] ? [`mod-${pair}`] : [])
                    ])];

                    setOpenModules(next);
                  } else {
                    setOpenModules(
                      values.filter(
                        (value) =>
                          value !== `mod-${clicked}` &&
                          value !== `mod-${pair}`
                      )
                    );
                  }
                }}
                className="w-full grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-5"
                data-testid="course-curriculum-accordion"
              >
                {c.curriculum.map((m, i) => (
                  <AccordionItem
                    key={i}
                    value={`mod-${i}`}
                    className="border border-zinc-800/80 rounded-2xl px-5 lg:px-6 bg-zinc-900/40 shadow-[0_8px_30px_rgba(0,0,0,0.12)] hover:border-brand-mint/25 hover:bg-zinc-900/60 transition-all duration-500 ease-out"
                  >
                    <AccordionTrigger
                      className="text-left py-6 font-display text-lg lg:text-xl uppercase text-white hover:text-brand-mint hover:no-underline transition-colors duration-300"
                      data-testid={`course-module-${i}`}
                    >
                      <span className="flex items-center gap-4 pr-4">
                        <span className="font-serif-accent text-brand-mint text-2xl leading-none shrink-0">
                          {String(i + 1).padStart(2, "0")}
                        </span>

                        <span className="leading-tight">{m.title}</span>
                      </span>
                    </AccordionTrigger>

                    <AccordionContent className="overflow-hidden text-zinc-400 text-sm lg:text-base leading-relaxed pl-11 pr-4 pb-6">
                      <div className="pt-1">
                        {m.details || "Detailed lessons revealed on enrolment."}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            ) : (
              c.modules.map((m, i) => (
                <Reveal key={m} delay={i * 0.05}>
                  <div className="grid grid-cols-12 gap-6 py-6 border-b border-zinc-900">
                    <div className="col-span-2 lg:col-span-1"><span className="font-serif-accent text-brand-mint text-3xl">{String(i + 1).padStart(2, "0")}</span></div>
                    <div className="col-span-10 lg:col-span-11 flex items-center justify-between">
                      <span className="font-display text-xl lg:text-2xl">{m}</span>
                      <GraduationCap size={16} className="text-zinc-600" />
                    </div>
                  </div>
                </Reveal>
              ))
            )}
          </div>
        </div>
      </section>

      {/* TOOLS YOU WILL MASTER */}
      {c.tools && c.tools.length > 0 && (
        <section className="bg-brand-bg py-24 lg:py-32 px-6 lg:px-12 border-t border-zinc-900" data-testid="course-tools-section">
          <div className="max-w-[1600px] mx-auto">
            <Reveal>
              <p className="eyebrow mb-6">Tools you'll master</p>
              <h2 className="font-display text-3xl lg:text-5xl tracking-tight uppercase max-w-3xl leading-[0.95]">
                Learn the <span className="font-serif-accent text-brand-mint lowercase">real</span> stack.
              </h2>
            </Reveal>
            <div className="mt-14 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {c.tools.map((t, i) => (
                <Reveal key={i} delay={i * 0.04}>
                  <div className="group flex flex-col items-center justify-center gap-4 p-6 lg:p-7 border border-zinc-900 rounded-2xl hover:border-brand-purple/50 bg-white/[0.02] transition-all duration-500 ease-out h-full min-h-[150px] hover:scale-[1.02]" data-testid={`course-tool-${i}`}>
                    {t.logo ? (
                      <img src={t.logo} alt={t.name} loading="lazy" className="h-12 w-auto max-w-[110px] object-contain transition-transform duration-500 ease-out group-hover:scale-105" />
                    ) : (
                      <span className="h-12 w-12 rounded-xl bg-brand-purple/20 border border-brand-purple/30 flex items-center justify-center font-display text-brand-purple text-xl transition-transform duration-500 ease-out group-hover:scale-105">{(t.name || "?")[0]}</span>
                    )}
                    <span className="text-zinc-300 text-sm text-center">{t.name}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* WHY CHOOSE THIS COURSE */}
      {((c.why_choose && c.why_choose.length > 0) || c.why_choose_intro) && (
        <section
          className="bg-brand-bg py-24 lg:py-32 px-6 lg:px-12 border-t border-zinc-900"
          data-testid="course-why-section"
        >
          <div className="max-w-[1600px] mx-auto">
            <Reveal>
              <p className="eyebrow mb-6">Why choose this course</p>

              <h2 className="font-display text-4xl lg:text-6xl tracking-tight uppercase max-w-4xl leading-[0.9]">
                Built for{" "}
                <span className="font-serif-accent text-brand-mint lowercase">
                  practical
                </span>{" "}
                learning.
              </h2>

              {c.why_choose_intro && (
                <p className="mt-7 text-zinc-400 text-base lg:text-lg leading-relaxed max-w-2xl">
                  {c.why_choose_intro}
                </p>
              )}
            </Reveal>

            {c.why_choose && c.why_choose.length > 0 && (() => {
              const bentoItems = [
                c.why_choose.find((item) => /practical|training/i.test(item.heading || "")) || c.why_choose[0],
                c.why_choose.find((item) => /month|months/i.test(item.heading || "")) || c.why_choose[1] || c.why_choose[0],
                c.why_choose.find((item) => /flexible|timing|schedule/i.test(item.heading || "")) || c.why_choose[2] || c.why_choose[0],
                c.why_choose.find((item) => /21,999|21999|price|₹|rs/i.test(item.heading || "")) || c.why_choose[3] || c.why_choose[0],
                c.why_choose.find((item) => /certificate|certified/i.test(item.heading || "")) || c.why_choose[4] || c.why_choose[0],
                c.why_choose.find((item) => /rating|star|review/i.test(item.heading || "")) || c.why_choose[5] || c.why_choose[0],
              ].filter(Boolean);

              return (
                <div className="mt-14 lg:mt-16 grid grid-cols-1 lg:grid-cols-12 gap-3 lg:gap-4">
                  {bentoItems.map((w, i) => {
                    const layouts = [
                      "lg:col-span-7 lg:row-span-2",
                      "lg:col-span-5",
                      "lg:col-span-5",
                      "lg:col-span-4",
                      "lg:col-span-4",
                      "lg:col-span-4",
                    ];

                    const layout = layouts[i] || "lg:col-span-4";

                    return (
                      <Reveal
                        key={`${w.heading}-${i}`}
                        delay={i * 0.06}
                        className={layout}
                      >
                        <div
                          className={`
                            group relative overflow-hidden rounded-[22px]
                            border border-zinc-800/80
                            bg-zinc-900/20
                            hover:border-brand-mint/30
                            transition-all duration-700 ease-out
                            h-full min-h-[230px]
                            ${i === 0 ? "lg:min-h-[480px]" : ""}
                            ${i === 1 || i === 2 ? "lg:min-h-[230px]" : ""}
                            ${i >= 3 ? "lg:min-h-[220px]" : ""}
                          `}
                          data-testid={`course-why-tile-${i}`}
                        >
                          {w.image && (
                            <div
                              className={`
                                absolute inset-0 overflow-hidden
                                ${i === 0 ? "opacity-70" : "opacity-45"}
                              `}
                            >
                              <img
                                src={w.image}
                                alt={w.heading || ""}
                                loading="lazy"
                                className="
                                  w-full h-full object-cover
                                  scale-100
                                  group-hover:scale-105
                                  transition-transform duration-[1400ms] ease-out
                                "
                              />
                            </div>
                          )}

                          <div
                            className="
                              absolute inset-0
                              bg-gradient-to-t
                              from-black/85
                              via-black/30
                              to-black/5
                              group-hover:from-black/75
                              transition-all duration-700
                            "
                          />

                          <div
                            className="
                              absolute -right-24 -top-24
                              w-64 h-64
                              rounded-full
                              bg-brand-purple/10
                              blur-3xl
                              group-hover:bg-brand-mint/10
                              transition-all duration-1000
                            "
                          />

                          <div
                            className={`
                              relative z-10
                              h-full flex flex-col justify-end
                              p-6 lg:p-7
                              ${i === 0 ? "lg:p-9" : ""}
                            `}
                          >
                            <h3
                              className={`
                                font-display
                                uppercase
                                tracking-tight
                                text-white
                                leading-[0.95]
                                ${i === 0 ? "text-3xl lg:text-5xl max-w-xl" : "text-2xl lg:text-3xl max-w-sm"}
                              `}
                            >
                              {w.heading}
                            </h3>

                            {w.description && (
                              <p
                                className={`
                                  mt-4
                                  text-zinc-300
                                  leading-relaxed
                                  ${i === 0 ? "text-base lg:text-lg max-w-xl" : "text-sm lg:text-base max-w-md"}
                                `}
                              >
                                {w.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </Reveal>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </section>
      )}

      {/* STUDENT PORTFOLIO */}
      {c.student_work && c.student_work.length > 0 && (
        <section className="bg-brand-bg py-24 lg:py-32 px-6 lg:px-12 border-t border-zinc-900" data-testid="course-student-work-section">
          <div className="max-w-[1600px] mx-auto">
            <Reveal>
              <p className="eyebrow mb-6">Student portfolio</p>
              <h2 className="font-display text-3xl lg:text-5xl tracking-tight uppercase max-w-3xl leading-[0.95]">
                Real work by <span className="font-serif-accent text-brand-mint lowercase">real students.</span>
              </h2>
            </Reveal>
            <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {c.student_work.map((s, i) => (
                <Reveal key={i} delay={i * 0.06}>
                  <div className="group" data-testid={`course-student-work-${i}`}>
                    <div className="aspect-[4/3] overflow-hidden rounded-2xl border border-zinc-900">
                      <img src={s.image} alt="" loading="lazy" className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-[1200ms]" />
                    </div>
                    {s.description && <p className="mt-4 text-zinc-400 text-sm leading-relaxed">{s.description}</p>}
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {c.faqs && c.faqs.length > 0 && (
        <section
          className="bg-brand-bg py-24 lg:py-32 px-6 lg:px-12 border-t border-zinc-900"
          data-testid="course-faq-section"
        >
          <div className="max-w-[1600px] mx-auto">
            <Reveal>
              <p className="eyebrow mb-6">Frequently asked</p>

              <h2 className="font-display text-3xl lg:text-5xl tracking-tight uppercase">
                Straight{" "}
                <span className="font-serif-accent text-brand-mint lowercase">
                  answers.
                </span>
              </h2>

              <p className="mt-6 text-zinc-400 text-base lg:text-lg leading-relaxed max-w-2xl">
                Everything you need to know about the course, from learning format
                and curriculum to enrolment and what you can expect.
              </p>
            </Reveal>

            <div className="mt-12 lg:mt-16 grid grid-cols-1 lg:grid-cols-2 gap-x-10">
              <div className="w-full">
                {c.faqs
                  .filter((_, i) => i % 2 === 0)
                  .map((faq, i) => {
                    const originalIndex = i * 2;

                    return (
                      <details
                        key={originalIndex}
                        className="group rounded-2xl px-5 lg:px-6 bg-zinc-900/20 transition-all duration-500 ease-out mb-4 overflow-hidden hover:bg-zinc-900/35 hover:shadow-[0_12px_30px_rgba(12,18,28,0.2)]"
                      >
                        <summary
                          className="flex min-h-[88px] cursor-pointer list-none py-6 lg:py-7 pr-8 font-display text-lg lg:text-xl uppercase text-white hover:text-brand-mint transition-colors duration-300 relative items-center"
                          data-testid={`course-faq-${originalIndex}`}
                        >
                          <span className="block leading-tight pr-8 flex-1">
                            {faq.question || faq.title || faq.q}
                          </span>

                          <span className="absolute right-0 top-1/2 -translate-y-1/2 text-zinc-500 text-xl transition-transform duration-300 group-open:rotate-45">
                            +
                          </span>
                        </summary>

                        <div className="grid grid-rows-[0fr] transition-[grid-template-rows] duration-500 ease-out group-open:grid-rows-[1fr]">
                          <div className="overflow-hidden">
                            <p className="text-zinc-400 text-sm lg:text-base leading-relaxed pb-6 max-w-xl">
                              {faq.answer || faq.description || faq.a}
                            </p>
                          </div>
                        </div>
                      </details>
                    );
                  })}
              </div>

              <div className="w-full">
                {c.faqs
                  .filter((_, i) => i % 2 === 1)
                  .map((faq, i) => {
                    const originalIndex = i * 2 + 1;

                    return (
                      <details
                        key={originalIndex}
                        className="group rounded-2xl px-5 lg:px-6 bg-zinc-900/20 transition-all duration-500 ease-out mb-4 overflow-hidden hover:bg-zinc-900/35 hover:shadow-[0_12px_30px_rgba(12,18,28,0.2)]"
                      >
                        <summary
                          className="flex min-h-[88px] cursor-pointer list-none py-6 lg:py-7 pr-8 font-display text-lg lg:text-xl uppercase text-white hover:text-brand-mint transition-colors duration-300 relative items-center"
                          data-testid={`course-faq-${originalIndex}`}
                        >
                          <span className="block leading-tight pr-8 flex-1">
                            {faq.question || faq.title || faq.q}
                          </span>

                          <span className="absolute right-0 top-1/2 -translate-y-1/2 text-zinc-500 text-xl transition-transform duration-300 group-open:rotate-45">
                            +
                          </span>
                        </summary>

                        <div className="grid grid-rows-[0fr] transition-[grid-template-rows] duration-500 ease-out group-open:grid-rows-[1fr]">
                          <div className="overflow-hidden">
                            <p className="text-zinc-400 text-sm lg:text-base leading-relaxed pb-6 max-w-xl">
                              {faq.answer || faq.description || faq.a}
                            </p>
                          </div>
                        </div>
                      </details>
                    );
                  })}
              </div>
            </div>

            <div className="mt-10">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 link-underline text-sm uppercase tracking-[0.24em] text-brand-mint"
              >
                Still curious? Ask us
                <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>
        </section>
      )}

      <section className="bg-brand-bg px-6 lg:px-12 py-10 lg:py-14 border-t border-zinc-900">
        <div className="max-w-[1600px] mx-auto">
          <div className="relative overflow-hidden rounded-[28px] bg-[#191919] min-h-[460px] lg:min-h-[500px]">
            <div className="
              absolute -left-20 -bottom-32
              w-[320px] h-[320px]
              rounded-[42%_58%_55%_45%]
              bg-brand-purple/90
              rotate-[-12deg]
            " />

            <div className="
              absolute -right-24 -top-28
              w-[310px] h-[310px]
              rounded-full
              border-[48px] border-brand-purple/90
            " />

            <div className="
              absolute
              left-[48%] top-[18%]
              w-[105px] h-[75px]
              border-2 border-brand-mint/60
              rounded-[55%_45%_60%_40%]
              rotate-[18deg]
            " />

            <div className="
              absolute
              right-[28%] top-[30%]
              w-28 h-28
              rounded-full
              border-2 border-brand-mint/40
            " />

            <div className="
              absolute
              left-[43%] bottom-[18%]
              w-3 h-3
              rounded-full
              bg-brand-mint
            " />

            <div className="
              relative z-10
              flex flex-col justify-center
              min-h-[460px] lg:min-h-[500px]
              px-8 py-12
              lg:px-16
            ">
              <Reveal>
                <p className="eyebrow mb-5">Your next step</p>

                <h2 className="
                  font-display uppercase
                  tracking-[-0.06em]
                  leading-[0.82]
                  text-[17vw]
                  sm:text-[12vw]
                  lg:text-[9vw]
                  xl:text-[135px]
                  text-white
                  max-w-[1050px]
                ">
                  Book a 
                  <br />
                  <span className="
                    font-serif-accent
                    text-brand-mint
                    lowercase
                    tracking-normal
                  ">
                    free demo.
                  </span>
                </h2>

                <p className="
                  mt-7
                  text-zinc-300
                  text-sm lg:text-base
                  leading-relaxed
                  max-w-[580px]
                ">
                  Stop learning digital marketing in theory. Build real skills,
                  work with real tools, and create campaigns you can actually put
                  to work.
                </p>
              </Reveal>
            </div>

            <div className="
              absolute
              right-8 bottom-8
              lg:right-16 lg:bottom-12
              z-20
            ">
              <Link
                to="/contact"
                aria-label="Talk to Dorado9"
                className="
                  group relative
                  block
                  w-[165px] h-[165px]
                  lg:w-[205px] lg:h-[205px]
                  rounded-full
                  bg-brand-purple
                  shadow-[0_0_55px_rgba(102,76,221,0.55)]
                  hover:shadow-[0_0_80px_rgba(102,76,221,0.75)]
                  hover:scale-105
                  transition-all duration-500
                "
              >
                <svg
                  viewBox="0 0 200 200"
                  className="
                    absolute inset-0
                    w-full h-full
                    animate-[spin_18s_linear_infinite]
                    group-hover:[animation-duration:10s]
                  "
                >
                  <defs>
                    <path
                      id="ctaCirclePath"
                      d="
                        M 100,100
                        m -76,0
                        a 76,76 0 1,1 152,0
                        a 76,76 0 1,1 -152,0
                      "
                    />
                  </defs>

                  <text
                    fill="#191919"
                    fontSize="10"
                    fontWeight="700"
                    letterSpacing="3"
                  >
                    <textPath href="#ctaCirclePath" startOffset="0%">
                      LET'S TALK • BOOK A CALL • LET'S TALK • BOOK A CALL •
                    </textPath>
                  </text>
                </svg>

                <span className="
                  absolute
                  left-1/2 top-1/2
                  -translate-x-1/2 -translate-y-1/2
                  w-[78px] h-[78px]
                  lg:w-[96px] lg:h-[96px]
                  rounded-full
                  bg-[#191919]
                  flex items-center justify-center
                ">
                  <ArrowUpRight
                    size={42}
                    strokeWidth={2.5}
                    className="
                      text-white
                      transition-transform duration-500
                      group-hover:translate-x-1 group-hover:-translate-y-1
                    "
                  />
                </span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
